import React, { useRef, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, Target, Shield, TrendingUp, Landmark, CheckCircle2, MapPin, Award, Briefcase, Home, DollarSign, BarChart3, PieChart } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { team } from '../data/mock';

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = '', prefix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
  
  useEffect(() => {
    if (isInView) {
      // Handle zero case
      if (numericValue === 0) {
        setCount(0);
        return;
      }
      
      let start = 0;
      const end = numericValue;
      const incrementTime = (duration * 1000) / end;
      const timer = setInterval(() => {
        start += 1;
        setCount(start);
        if (start >= end) clearInterval(timer);
      }, Math.max(incrementTime, 10));
      return () => clearInterval(timer);
    }
  }, [isInView, numericValue, duration]);
  
  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
};

const AboutPage = () => {
  const heroRef = useRef(null);
  const [activePrinciple, setActivePrinciple] = useState(0);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const scaleInVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Timeline milestones with icons
  const milestones = [
    { year: '2005', title: 'Founded in Houston', description: 'Began residential real estate investment operations in the Greater Houston market.', icon: Home },
    { year: '2012', title: 'Multifamily Expansion', description: 'Strategic shift into larger-scale multifamily value-add investments.', icon: Building2 },
    { year: '2019', title: 'Commercial & Retail', description: 'Portfolio diversification into commercial property sectors across Texas.', icon: Briefcase },
    { year: '2024', title: 'Institutional Growth', description: 'Launched 506(c) private equity fund for investors.', icon: TrendingUp }
  ];

  // Core values with expanded descriptions
  const coreValues = [
    { 
      icon: Target, 
      title: 'Disciplined Investment', 
      description: 'Our data-driven approach combines rigorous market analysis with proprietary deal sourcing to identify undervalued assets with significant upside potential. We evaluate every opportunity against strict investment criteria, focusing on properties where our operational expertise can drive measurable value creation. This disciplined methodology has allowed us to consistently identify opportunities that others overlook, delivering superior risk-adjusted returns across market cycles.'
    },
    { 
      icon: Shield, 
      title: 'Capital Preservation', 
      description: 'Protecting investor capital is our primary responsibility. We implement conservative leverage strategies, maintain adequate reserves, and structure each investment with multiple layers of downside protection. Our risk management framework includes comprehensive due diligence, stress testing under various market scenarios, and proactive asset monitoring. This approach has enabled us to preserve capital through market volatility while positioning for growth during favorable conditions.'
    },
    { 
      icon: Users, 
      title: 'Investor Alignment', 
      description: 'We believe the best partnerships are built on aligned interests. Our principals invest meaningful personal capital alongside our investors in every deal, ensuring we share in both the risks and rewards. Our fee structure is performance-based, meaning we only succeed when our investors succeed. We maintain transparent communication with regular reporting, investor portals, and direct access to our leadership team throughout the investment lifecycle.'
    },
    { 
      icon: TrendingUp, 
      title: 'Value Creation', 
      description: 'Our hands-on operational expertise transforms underperforming assets into institutional-quality properties. We implement comprehensive value-add strategies including capital improvements, operational efficiencies, revenue optimization, and strategic repositioning. Our vertically integrated platform—with in-house property management, leasing, and construction oversight—allows us to execute these strategies efficiently while maintaining quality control at every stage of the investment.'
    }
  ];

  // Vertically integrated capabilities with icons
  const capabilities = [
    { name: 'Acquisitions & Due Diligence', icon: Target },
    { name: 'Asset Management', icon: BarChart3 },
    { name: 'Property Management', icon: Building2 },
    { name: 'Construction Management', icon: Briefcase },
    { name: 'Investor Relations', icon: Users },
    { name: 'Financial Reporting', icon: PieChart }
  ];

  // Who we serve
  const investorTypes = [
    { title: 'High Net Worth Individuals', description: 'investors seeking institutional-quality real estate exposure with $100K+ minimum.', icon: Users },
    { title: 'Family Offices', description: 'Multi-generational wealth preservation through diversified real estate investments.', icon: Home },
    { title: 'Institutional Investors', description: 'Pension funds, endowments, and foundations seeking stable, risk-adjusted returns.', icon: Landmark }
  ];

  // Key metrics for infographic
  const keyMetrics = [
    { value: '18', suffix: '+', label: 'Years Experience', icon: Award },
    { value: '100', suffix: '+', label: 'Deals Completed', icon: Briefcase },
    { value: '70', suffix: '+', label: 'Successful Exits', icon: TrendingUp },
    { value: '0', suffix: '', label: 'Investor Losses', icon: Shield }
  ];

  // Investment allocation data
  const investmentAllocation = [
    { category: 'Multifamily', percentage: 55, color: '#D4AF37' },
    { category: 'Commercial', percentage: 25, color: '#F5D76E' },
    { category: 'Retail', percentage: 15, color: '#B8962E' },
    { category: 'Other', percentage: 5, color: '#8B7355' }
  ];

  return (
    <>
      <Helmet>
        <title>About ASAP Capital | Houston Private Equity Real Estate Firm</title>
        <meta name="description" content="Learn about ASAP Capital, a Houston-based private equity real estate firm specializing in value-add multifamily and commercial investments across Texas." />
        <meta property="og:title" content="About ASAP Capital | Houston Private Equity Real Estate Firm" />
        <meta property="og:description" content="Learn about ASAP Capital, a Houston-based private equity real estate firm specializing in value-add multifamily and commercial investments across Texas." />
        <link rel="canonical" href="/about" />
      </Helmet>

      <div className="bg-[#0A1628] min-h-screen">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section ref={heroRef} className="relative pt-36 pb-28 lg:pt-44 lg:pb-36 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D4AF37]/3 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl mx-auto text-center"
              >
                {/* Pill badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <Building2 className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Our Story</span>
                </motion.div>

                {/* H1 - Split into two visual groups for better hierarchy */}
                <motion.div className="mb-12" variants={itemVariants}>
                  {/* Main statement */}
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.3] lg:leading-[1.35] mb-4">
                    Building Value Through
                  </h1>
                  
                  {/* Supporting line with highlight */}
                  <div className="flex flex-wrap items-baseline justify-center gap-x-4 gap-y-2">
                    <span className="relative inline-block">
                      <span className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                        Houston Real Estate
                      </span>
                      <motion.span
                        className="absolute -bottom-2 left-0 h-[2px] bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: "100%" }}
                        transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
                      />
                    </span>
                    <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white/80 mt-2 sm:mt-0">
                      Since 2005
                    </span>
                  </div>
                </motion.div>

                {/* Description with increased spacing */}
                <motion.p 
                  className="text-base lg:text-lg text-white/55 leading-relaxed mb-12 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  ASAP Capital is a Houston-based private equity real estate firm with nearly two decades of experience 
                  acquiring, repositioning, and managing value-add properties across the Greater Houston metropolitan area and Texas.
                </motion.p>

                {/* CTA buttons with clear separation */}
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                      Schedule a Consultation
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                  <Link to="/team">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                      Meet Our Team
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== HOUSTON IMAGE BANNER ==================== */}
          <section className="relative h-[300px] lg:h-[400px] overflow-hidden mt-8">
            {/* Image with overlay */}
            <div className="absolute inset-0">
              <motion.img
                src="https://images.unsplash.com/photo-1692154600992-463fa9b27abd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxIb3VzdG9uJTIwc2t5bGluZSUyMGRvd250b3duJTIwYWVyaWFsfGVufDB8fHx8MTc2OTE4OTE3Mnww&ixlib=rb-4.1.0&q=85"
                alt="Houston skyline at night"
                className="w-full h-full object-cover"
                initial={{ scale: 1.1 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/60 to-[#0A1628]/30" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/80 to-transparent" />
            </div>
            
            {/* Content overlay */}
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-[1200px] mx-auto px-6 lg:px-12 w-full">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <span className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-2 block">Houston, Texas</span>
                  <h3 className="text-2xl lg:text-4xl font-bold text-white mb-3">
                    America's #4 Largest City
                  </h3>
                  <p className="text-white/70 max-w-md text-sm lg:text-base">
                    Home to Fortune 500 companies, world-class medical centers, and a thriving real estate market.
                  </p>
                </motion.div>
              </div>
            </div>
          </section>

          {/* ==================== FOUNDED IN HOUSTON ==================== */}
          <section className="py-16 lg:py-24 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
              >
                {/* Content */}
                <div>
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Our Beginning
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-6"
                    variants={itemVariants}
                  >
                    Founded in Houston with a Focus on Value-Add Real Estate
                  </motion.h2>
                  <motion.div className="space-y-4 text-white/60 leading-relaxed" variants={itemVariants}>
                    <p>
                      ASAP Capital was founded in 2005 with a clear vision: to build long-term wealth through disciplined 
                      real estate investment in one of America's most dynamic markets. Starting with single-family investments, 
                      our founders recognized the tremendous opportunity in Houston's growing metropolitan area.
                    </p>
                    <p>
                      As a Houston-based real estate investment company, we've spent nearly two decades developing deep expertise 
                      in the Greater Houston real estate market. Our intimate knowledge of local neighborhoods, economic drivers, 
                      and growth patterns gives us a significant competitive advantage in identifying undervalued opportunities.
                    </p>
                    <p>
                      Today, ASAP Capital has evolved into a vertically integrated real estate firm managing a diverse portfolio 
                      of multifamily and commercial assets across Texas, while maintaining our founding commitment to disciplined, 
                      value-focused investing.
                    </p>
                  </motion.div>
                </div>

                {/* Animated Stats Infographic */}
                <motion.div 
                  className="relative"
                  variants={itemVariants}
                >
                  <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-5">
                      <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 2px 2px, #D4AF37 1px, transparent 0)`,
                        backgroundSize: '32px 32px'
                      }} />
                    </div>
                    
                    <div className="relative z-10 grid grid-cols-2 gap-6">
                      {keyMetrics.map((metric, index) => (
                        <motion.div 
                          key={metric.label}
                          className="text-center p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-[#D4AF37]/20 transition-colors group"
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ y: -4 }}
                        >
                          <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                            <metric.icon className="w-6 h-6 text-[#D4AF37]" />
                          </div>
                          <span className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent block">
                            <AnimatedCounter value={metric.value} suffix={metric.suffix} />
                          </span>
                          <p className="text-white/50 text-sm mt-2">{metric.label}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  {/* Decorative elements */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#D4AF37]/10 rounded-full blur-2xl" />
                  <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-[#D4AF37]/5 rounded-full blur-xl" />
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== INVESTMENT ALLOCATION INFOGRAPHIC ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Portfolio Composition
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Strategic Asset Allocation
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-xl mx-auto"
                    variants={itemVariants}
                  >
                    Our diversified portfolio strategy balances growth opportunities with stable, income-producing assets.
                  </motion.p>
                </div>

                {/* Allocation Visual */}
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Circular Progress Visualization */}
                  <motion.div 
                    className="relative flex justify-center"
                    variants={scaleInVariants}
                  >
                    <div className="relative w-64 h-64 lg:w-80 lg:h-80">
                      {/* Central circle */}
                      <div className="absolute inset-8 lg:inset-12 rounded-full bg-[#0A1628] flex items-center justify-center flex-col border border-white/10">
                        <DollarSign className="w-8 h-8 text-[#D4AF37] mb-2" />
                        <span className="text-2xl lg:text-3xl font-bold text-white">$25M</span>
                        <span className="text-xs text-white/50">Target Raise</span>
                      </div>
                      
                      {/* Animated ring segments */}
                      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                        {investmentAllocation.map((item, index) => {
                          const prevPercentages = investmentAllocation.slice(0, index).reduce((acc, curr) => acc + curr.percentage, 0);
                          const circumference = 2 * Math.PI * 42;
                          const dashoffset = circumference - (item.percentage / 100) * circumference;
                          const rotation = (prevPercentages / 100) * 360;
                          
                          return (
                            <motion.circle
                              key={item.category}
                              cx="50"
                              cy="50"
                              r="42"
                              fill="none"
                              stroke={item.color}
                              strokeWidth="12"
                              strokeLinecap="round"
                              strokeDasharray={circumference}
                              initial={{ strokeDashoffset: circumference }}
                              whileInView={{ strokeDashoffset: dashoffset }}
                              viewport={{ once: true }}
                              transition={{ delay: 0.3 + index * 0.2, duration: 1, ease: "easeOut" }}
                              style={{ 
                                transform: `rotate(${rotation}deg)`,
                                transformOrigin: '50% 50%'
                              }}
                            />
                          );
                        })}
                      </svg>
                    </div>
                  </motion.div>

                  {/* Legend and details */}
                  <motion.div 
                    className="space-y-4"
                    variants={containerVariants}
                  >
                    {investmentAllocation.map((item, index) => (
                      <motion.div
                        key={item.category}
                        className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#D4AF37]/20 transition-all group"
                        variants={itemVariants}
                        whileHover={{ x: 8 }}
                      >
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: item.color }}
                        />
                        <div className="flex-1">
                          <h4 className="text-white font-medium group-hover:text-[#D4AF37] transition-colors">
                            {item.category}
                          </h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full rounded-full"
                              style={{ backgroundColor: item.color }}
                              initial={{ width: 0 }}
                              whileInView={{ width: `${item.percentage}%` }}
                              viewport={{ once: true }}
                              transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }}
                            />
                          </div>
                          <span className="text-[#D4AF37] font-semibold w-12 text-right">{item.percentage}%</span>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== EVOLUTION TIMELINE ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="text-center mb-12"
              >
                <motion.span 
                  className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                  variants={itemVariants}
                >
                  Our Evolution
                </motion.span>
                <motion.h2 
                  className="text-2xl lg:text-3xl font-bold text-white mb-4"
                  variants={itemVariants}
                >
                  From Single-Family to Institutional Multifamily Investments
                </motion.h2>
                <motion.p 
                  className="text-white/50 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  Our journey reflects a disciplined evolution, expanding our capabilities while maintaining our core focus on value creation.
                </motion.p>
              </motion.div>

              {/* Timeline */}
              <div className="relative">
                {/* Timeline line - Desktop */}
                <div className="hidden lg:block absolute top-[52px] left-[12.5%] right-[12.5%] h-[3px] bg-white/10 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37]"
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  />
                </div>

                <div className="grid lg:grid-cols-4 gap-8">
                  {milestones.map((milestone, index) => (
                    <motion.div
                      key={milestone.year}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 }}
                      className="relative"
                    >
                      {/* Animated Dot with Icon */}
                      <motion.div 
                        className="hidden lg:flex w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-full absolute top-[25px] left-1/2 -translate-x-1/2 border-4 border-[#0A1628] shadow-lg shadow-[#D4AF37]/30 z-10 items-center justify-center"
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + index * 0.15, type: "spring", stiffness: 200 }}
                      >
                        <milestone.icon className="w-6 h-6 text-[#0A1628]" />
                      </motion.div>
                      
                      {/* Card */}
                      <motion.div 
                        className="lg:mt-24 bg-[#111D35]/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group relative overflow-hidden"
                        whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(212, 175, 55, 0.1)" }}
                      >
                        {/* Hover glow */}
                        <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        
                        <div className="relative z-10">
                          {/* Mobile icon */}
                          <div className="lg:hidden w-12 h-12 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-xl flex items-center justify-center mb-3">
                            <milestone.icon className="w-5 h-5 text-[#D4AF37]" />
                          </div>
                          
                          <span className="text-3xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
                            {milestone.year}
                          </span>
                          <h3 className="text-white font-semibold mt-2 mb-2 group-hover:text-[#D4AF37] transition-colors">
                            {milestone.title}
                          </h3>
                          <p className="text-white/50 text-sm leading-relaxed">
                            {milestone.description}
                          </p>
                        </div>
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ==================== LEADERSHIP SECTION ==================== */}
          <section className="py-16 lg:py-24 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Our Leadership
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Leadership with Deep Market and Operational Expertise
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Our experienced multifamily investors bring decades of combined expertise in real estate acquisitions, 
                    asset management, and value creation across Texas markets.
                  </motion.p>
                </div>

                {/* Leadership Preview */}
                <motion.div 
                  className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto"
                  variants={itemVariants}
                >
                  {team.slice(0, 2).map((member, index) => (
                    <motion.div
                      key={member.name}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group"
                      whileHover={{ y: -5 }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-[#D4AF37]/20 flex-shrink-0 bg-[#111D35]">
                          <img 
                            src={member.image} 
                            alt={member.name}
                            className="w-full h-full object-cover object-top"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold text-lg group-hover:text-[#D4AF37] transition-colors">
                            {member.name}
                          </h3>
                          <p className="text-[#D4AF37] text-sm mb-2">{member.title}</p>
                          <p className="text-white/50 text-sm line-clamp-3">{member.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div 
                  className="text-center mt-8"
                  variants={itemVariants}
                >
                  <Link to="/team">
                    <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-6 py-3 rounded-full text-sm">
                      View Full Team
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== VERTICALLY INTEGRATED ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                {/* Capabilities Grid with Icons */}
                <motion.div variants={itemVariants}>
                  <div className="grid grid-cols-2 gap-4">
                    {capabilities.map((capability, index) => (
                      <motion.div
                        key={capability.name}
                        className="bg-[#111D35]/50 backdrop-blur-sm rounded-xl p-5 border border-white/10 group hover:border-[#D4AF37]/30 transition-all duration-300"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.08 }}
                        whileHover={{ y: -4, boxShadow: "0 12px 24px rgba(212, 175, 55, 0.08)" }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/20 transition-colors">
                            <capability.icon className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <span className="text-white/70 text-sm font-medium leading-tight pt-2 group-hover:text-white transition-colors">{capability.name}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Additional visual element */}
                  <motion.div 
                    className="mt-6 p-4 rounded-xl bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20"
                    variants={itemVariants}
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-white/70 text-sm">100% In-House Operations — No Third-Party Dependencies</span>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Content */}
                <div>
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Our Approach
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-6"
                    variants={itemVariants}
                  >
                    A Vertically Integrated Investment Platform
                  </motion.h2>
                  <motion.div className="space-y-4 text-white/60 leading-relaxed" variants={itemVariants}>
                    <p>
                      As a vertically integrated real estate firm in Houston, ASAP Capital controls every aspect of the 
                      investment lifecycle. From initial acquisition through asset management and eventual disposition, 
                      our in-house capabilities ensure alignment, efficiency, and value creation at every stage.
                    </p>
                    <p>
                      This integrated approach allows us to respond quickly to market opportunities, maintain strict quality 
                      control over our properties, and deliver consistent returns to our investors. We don't rely on 
                      third-party managers—we are the operators.
                    </p>
                  </motion.div>
                  
                  {/* Key benefits */}
                  <motion.div 
                    className="mt-6 flex flex-wrap gap-3"
                    variants={itemVariants}
                  >
                    {['Speed to Market', 'Quality Control', 'Cost Efficiency', 'Alignment'].map((benefit, i) => (
                      <span 
                        key={benefit}
                        className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm"
                      >
                        {benefit}
                      </span>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MISSION & VISION ==================== */}
          <section className="py-16 lg:py-24 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Our Principles
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Mission, Vision, and Investor Alignment
                  </motion.h2>
                </div>

                {/* Vertical Tabs Layout */}
                <div className="grid lg:grid-cols-[300px_1fr] gap-6 lg:gap-8 items-stretch">
                  {/* Left - Vertical Tab Navigation */}
                  <div className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                    {coreValues.map((value, index) => (
                      <motion.button
                        key={value.title}
                        onClick={() => setActivePrinciple(index)}
                        className={`flex items-center gap-3 px-4 py-4 rounded-xl text-left transition-all duration-300 min-w-[200px] lg:min-w-0 ${
                          activePrinciple === index
                            ? 'bg-[#D4AF37] text-[#0A1628]'
                            : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <value.icon className={`h-5 w-5 flex-shrink-0 ${
                          activePrinciple === index ? 'text-[#0A1628]' : 'text-[#D4AF37]'
                        }`} />
                        <span className="font-medium text-sm lg:text-base whitespace-nowrap lg:whitespace-normal">
                          {value.title}
                        </span>
                      </motion.button>
                    ))}
                  </div>

                  {/* Right - Content Card - Stretches to match tabs height */}
                  <motion.div
                    key={activePrinciple}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 lg:p-10 border border-white/10 flex flex-col h-full min-h-[300px] lg:min-h-0"
                  >
                    <div className="flex flex-col h-full">
                      {/* Icon */}
                      <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mb-6">
                        {React.createElement(coreValues[activePrinciple].icon, {
                          className: "h-8 w-8 text-[#D4AF37]"
                        })}
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                        {coreValues[activePrinciple].title}
                      </h3>
                      
                      {/* Description - Takes remaining space */}
                      <p className="text-white/60 text-sm lg:text-base leading-relaxed flex-1">
                        {coreValues[activePrinciple].description}
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== SERVING INVESTORS ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Who We Serve
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Serving investors Across Greater Houston and Texas
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Our private equity real estate investments are designed for sophisticated investors seeking 
                    institutional-quality exposure to the Texas commercial real estate market.
                  </motion.p>
                </div>

                {/* Investor Types */}
                <div className="grid md:grid-cols-3 gap-6">
                  {investorTypes.map((type, index) => (
                    <motion.div
                      key={type.title}
                      className="bg-[#111D35]/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group relative overflow-hidden"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -8, boxShadow: "0 25px 50px rgba(212, 175, 55, 0.1)" }}
                    >
                      {/* Animated background gradient */}
                      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <div className="relative z-10">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                          <type.icon className="h-7 w-7 text-[#D4AF37]" />
                        </div>
                        <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-[#D4AF37] transition-colors">{type.title}</h3>
                        <p className="text-white/50 text-sm leading-relaxed">{type.description}</p>
                      </div>
                      
                      {/* Corner accent */}
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-[#D4AF37]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== CTA SECTION ==================== */}
          <section className="py-16 lg:py-24 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-10 lg:p-14 border border-white/10 text-center relative overflow-hidden"
              >
                {/* Background glow */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-[100px]" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#D4AF37]/5 rounded-full blur-[80px]" />
                
                <div className="relative z-10">
                  <MapPin className="h-10 w-10 text-[#D4AF37] mx-auto mb-4" />
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                    Ready to Explore Investment Opportunities?
                  </h2>
                  <p className="text-white/50 max-w-xl mx-auto mb-8">
                    Schedule a confidential consultation with our investor relations team to learn how ASAP Capital 
                    can help you achieve your real estate investment goals.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/contact">
                      <button className="cta-contact px-8 py-3 h-12 rounded-full text-sm">
                        Schedule Consultation
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </Link>
                    <Link to="/contact">
                      <button className="cta-contact px-8 py-3 h-12 rounded-full text-sm">
                        Contact Us
                      </button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AboutPage;
